import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import CardDetailModal from '../components/CardDetailModal';
import { Search, User, Mail, Building2, Phone, ChevronRight, RefreshCw, Plus } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactsList = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCard, setSelectedCard] = useState(null);

  const fetchContacts = useCallback(async () => {
    try {
      let url = `${BACKEND_URL}/api/contacts`;
      if (searchTerm) {
        url += `?search=${encodeURIComponent(searchTerm)}`;
      }
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      }
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchContacts();
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [fetchContacts]);

  const handleCardClick = async (cardId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/cards/${cardId}`, { credentials: 'include' });
      if (response.ok) {
        const card = await response.json();
        setSelectedCard(card);
      }
    } catch (error) {
      console.error('Failed to fetch card:', error);
    }
  };

  const handleCardUpdate = () => {
    fetchContacts();
  };

  const getFullName = (contact) => {
    // Try first_name + last_name first, then fall back to name field
    const parts = [contact.first_name, contact.last_name].filter(Boolean);
    if (parts.length > 0) {
      return parts.join(' ');
    }
    // Fall back to legacy name field
    if (contact.name && contact.name.trim()) {
      return contact.name.trim();
    }
    // Last resort - show email if available
    if (contact.email && contact.email.trim()) {
      return contact.email;
    }
    return 'Unknown';
  };

  const getRoleLabel = (role) => {
    const roleLabels = {
      'main_contact': 'Main Contact',
      'billing': 'Billing Contact',
      'finance_contact': 'Finance Contact',
      'event_contact': 'Event Contact',
      'sponsor_contact': 'Sponsor Contact',
      'cmo': 'CMO',
      'ceo': 'CEO',
      'other': 'Other'
    };
    return roleLabels[role] || role || 'Contact';
  };

  if (loading) {
    return (
      <AppLayout title="Contacts">
        <div className="text-[#A2FF00]">Loading...</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Contacts">
      {/* Header with Add Button */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Contacts
          </h1>
          <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>View and manage all contacts</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/nurturing')}
            className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black font-bold rounded-sm transition-colors`}
            title="Select a company to add a contact"
          >
            <Plus className="w-4 h-4" />
            Add Contact
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4 mb-6`}>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[300px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, company, or title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full ${isDark ? 'bg-black border-white/20 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'} border rounded-sm pl-10 pr-4 py-2 focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00] outline-none`}
            />
          </div>
          <button
            onClick={fetchContacts}
            className={`p-2 ${isDark ? 'text-gray-400 hover:text-[#A2FF00]' : 'text-gray-500 hover:text-green-600'} transition-colors`}
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
        <div className="mt-3 text-sm text-gray-500">
          {contacts.length} {contacts.length === 1 ? 'contact' : 'contacts'} found
        </div>
      </div>

      {/* Contacts List */}
      {contacts.length === 0 ? (
        <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-8 text-center`}>
          <User className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">
            {searchTerm ? 'No contacts match your search' : 'No contacts in the database yet'}
          </p>
        </div>
      ) : (
        <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm overflow-hidden`}>
          <table className="w-full">
            <thead className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <tr>
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Name</th>
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Email</th>
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Phone</th>
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Company</th>
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Role</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact, index) => (
                <tr
                  key={`${contact.card_id}-${contact.role}-${index}`}
                  onClick={() => handleCardClick(contact.card_id)}
                  className={`border-b ${isDark ? 'border-white/5 hover:bg-white/5' : 'border-gray-100 hover:bg-gray-50'} cursor-pointer transition-colors`}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full ${isDark ? 'bg-[#A2FF00]/10' : 'bg-green-100'} flex items-center justify-center flex-shrink-0`}>
                        <User className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
                      </div>
                      <div>
                        <div className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium`}>
                          {getFullName(contact)}
                        </div>
                        {contact.title && (
                          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {contact.title}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    {contact.email ? (
                      <div className={`${isDark ? 'text-gray-300' : 'text-gray-600'} text-sm flex items-center gap-1`}>
                        <Mail className="w-3 h-3 text-gray-400" />
                        {contact.email}
                      </div>
                    ) : (
                      <span className="text-gray-500 text-sm">-</span>
                    )}
                  </td>
                  <td className="p-4">
                    {contact.phone ? (
                      <div className={`${isDark ? 'text-gray-300' : 'text-gray-600'} text-sm flex items-center gap-1`}>
                        <Phone className="w-3 h-3 text-gray-400" />
                        {contact.phone}
                      </div>
                    ) : (
                      <span className="text-gray-500 text-sm">-</span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className={`flex items-center gap-1 ${isDark ? 'text-gray-300' : 'text-gray-600'} text-sm`}>
                      <Building2 className="w-3 h-3 text-gray-400" />
                      {contact.company_name || '-'}
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 text-xs rounded ${isDark ? 'bg-[#A2FF00]/10 text-[#A2FF00]' : 'bg-green-100 text-green-700'}`}>
                      {getRoleLabel(contact.role)}
                    </span>
                  </td>
                  <td className="p-4">
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Card Detail Modal */}
      {selectedCard && (
        <CardDetailModal
          card={selectedCard}
          isOpen={!!selectedCard}
          onClose={() => setSelectedCard(null)}
          onUpdate={handleCardUpdate}
          boardType="member_tracking"
          columnName={null}
          defaultTab="contacts"
        />
      )}
    </AppLayout>
  );
};

export default ContactsList;
